import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import {
  ArrowLeft,
  Package,
  FileText,
  Check,
  Minus,
  Clock,
  User,
  Cpu,
  Upload,
  Mail,
  Loader2,
  Plus,
  StickyNote,
  Building,
  MessageSquare,
  Camera,
  ExternalLink,
  Reply,
  ShieldCheck,
} from 'lucide-react'
import { ConsumerShell } from '@/consumer/components/consumer-shell'
import { formatCurrency, useFormattingContext, useFormatters } from '@/shared/lib/formatting'
import {
  useConsumerClaimDetail,
  useConsumerClaimContact,
  useConsumerClaimExecuteAction,
  useSubmitClaimEvidence,
  type WorkflowStateInfo,
  type ActionLogEntry,
  type ConsumerAction,
} from '@/consumer/hooks/use-consumer-claims'
import { computeVisitedStateKeys } from '@/shared/lib/workflows/visited-states'
import { WorkflowActionIcon } from '@/shared/components/workflows/action-icon'
import { useConsumerFileUpload } from '@/consumer/hooks/use-registration'
import { DetailCard, FieldRow } from '@/admin/components/shared/detail-card'
import { EvidenceFileList } from '@/shared/components/evidence/evidence-file-list'
import { ConsumerEvidenceChecklist } from '@/consumer/components/evidence-checklist'
import { evidenceSlotAnchorId } from '@/consumer/lib/evidence-slot-anchor'
import {
  SessionShortfallError,
  type SessionShortfall,
} from '@/shared/lib/evidence/session-api'
import { SettlementSummaryCard } from '@/consumer/components/settlement-summary-card'
import { WarrantyBadge } from '@/consumer/components/product-card'
import { StatusBadge } from '@/admin/components/shared/status-badge'
import { ActionEntryDialog } from '@/shared/components/action-entry-dialog'
import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Label } from '@/shared/components/ui/label'
import { Textarea } from '@/shared/components/ui/textarea'
import { cn } from '@/shared/lib/utils'
import {
  normaliseClaimField,
  useForm,
  formatDisplayValue,
  defineField,
  type FormFieldMeta,
} from '@/shared/lib/forms'
import { buildFormFieldFromMeta } from '@/shared/lib/forms/generated-metadata'
import {
  buildClaimFieldItem,
  inferFieldType,
} from '@/admin/pages/claims/claim-detail-helpers'
import { titleCaseKey } from '@/shared/lib/humanise'
import {
  isEntityFileObject,
  entityFileDisplayUrl,
  entityFileDisplayName,
} from '@/admin/lib/entity-card-display'
import type { ConsumerActionFieldMeta } from '@/consumer/hooks/use-consumer-claims'
import api from '@/consumer/lib/api'
import { NotificationsPanel } from '@/admin/components/notifications-panel'
import {
  useConsumerNotifications,
  useMarkConsumerNotificationsRead,
  useConsumerNotificationItemAction,
} from '@/consumer/hooks/use-consumer-notifications'

// ── Constants ──────────────────────────────────────────

import { toastApiError } from '@/shared/lib/api-error'

/** Fields that hold file/image values */
const FILE_FIELDS = new Set(['photos', 'photo', 'evidence', 'image', 'images', 'document', 'attachment'])

// ── Helpers ─────────────────────────────────────────────

function getLatestIncomingMessageEntry(history: ActionLogEntry[]) {
  const incomingEntries = history.filter((entry) => {
    const params = entry.parameters as Record<string, unknown> | null
    if (!params) return false

    const senderRole = params.sender_role
    const isInternal = params.is_internal
    const message = params.message

    return (
      typeof message === 'string'
      && message.trim().length > 0
      && isInternal !== true
      && senderRole !== 'consumer'
      && (entry.action_key === 'consumer_contact' || entry.action_key === 'note')
    )
  })

  return incomingEntries.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  )[0] ?? null
}

// ── Workflow Stepper ────────────────────────────────────

function WorkflowStepper({
  states,
  currentStateKey,
  visitedStateKeys,
}: {
  states: WorkflowStateInfo[]
  currentStateKey: string
  /** States the claim ACTUALLY visited (from action-log history). When
   *  supplied, a prior-but-unvisited state renders SKIPPED instead of
   *  completed. Omitted for the monotonic stage tracker (which never skips). */
  visitedStateKeys?: ReadonlySet<string>
}) {
  const { t } = useTranslation()
  const sorted = [...states].sort((a, b) => (a.is_initial ? -1 : b.is_initial ? 1 : 0))
  const currentIdx = sorted.findIndex((s) => s.key === currentStateKey)

  return (
    <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-6 py-4">
      <div className="flex items-center gap-0 overflow-x-auto px-[3px] py-[3px]">
        {sorted.map((state, idx) => {
          const isCurrent = state.key === currentStateKey
          const isPrior = idx < currentIdx
          // A prior state is completed only when the claim actually visited it;
          // a prior-but-unvisited state was shortcut past and renders SKIPPED.
          // With no visited-set (the monotonic stage tracker) keep the
          // position-based behaviour so every prior stage stays completed.
          const isCompleted =
            isPrior && (!visitedStateKeys || visitedStateKeys.has(state.key))
          const isSkipped =
            isPrior && !!visitedStateKeys && !visitedStateKeys.has(state.key)
          const isTerminalError = state.is_terminal && state.key === 'rejected'
          const currentState = sorted.find((s) => s.key === currentStateKey)

          return (
            <div
              key={state.uuid}
              data-state-key={state.key}
              data-node-status={
                isCurrent
                  ? 'current'
                  : isCompleted
                    ? 'completed'
                    : isSkipped
                      ? 'skipped'
                      : 'future'
              }
              className="flex items-center shrink-0"
            >
              <div className="flex flex-col items-center gap-1.5">
                <div
                  title={
                    isSkipped
                      ? t(
                          'claims.stepper.skipped',
                          'Skipped - this claim did not pass through this stage',
                        )
                      : undefined
                  }
                  className={cn(
                    'flex items-center justify-center rounded-full transition-all duration-200',
                    isCurrent ? 'h-8 w-8 border-2 shadow-[0_0_0_3px_var(--rule)]' : 'h-6 w-6 border-[1.5px]',
                    isCurrent
                      ? isTerminalError
                        ? 'border-[var(--status-error)] bg-[var(--status-error)]'
                        : currentState?.is_terminal
                          ? 'border-[var(--status-ok)] bg-[var(--status-ok)]'
                          : 'border-[var(--green)] bg-[var(--green)]'
                      : isCompleted
                        ? 'border-transparent bg-[var(--green)]'
                        : isSkipped
                          ? 'border-dashed border-[var(--rule)] bg-transparent opacity-60'
                          : 'border-[var(--rule)] bg-[var(--bg)]',
                  )}
                >
                  {isCompleted && <Check className="relative h-3 w-3 text-white" />}
                  {isSkipped && <Minus className="relative h-2.5 w-2.5 text-[var(--ink-3)]" />}
                  {isCurrent && <div className="relative h-2.5 w-2.5 rounded-full bg-white" />}
                </div>
                <span
                  className={cn(
                    'whitespace-nowrap text-[10px] tracking-[0.04em]',
                    isCurrent
                      ? isTerminalError
                        ? 'font-medium text-[var(--status-error)]'
                        : currentState?.is_terminal
                          ? 'font-medium text-[var(--status-ok)]'
                          : 'font-medium text-[var(--green)]'
                      : isCompleted
                        ? 'text-[var(--ink)]'
                        : isSkipped
                          ? 'text-[var(--ink-3)] line-through opacity-60'
                          : 'text-[var(--ink-3)]',
                  )}
                >
                  {state.label}
                </span>
              </div>
              {idx < sorted.length - 1 && (
                <div
                  className={cn(
                    'mx-2 mb-[18px] h-[2px] w-8 rounded-[1px]',
                    isCompleted ? 'bg-[var(--green)]' : 'bg-[var(--rule)]',
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Timeline Entry ──────────────────────────────────────

const SENDER_ROLE_LABELS: Record<string, string> = {
  consumer: 'You',
  dealer: 'Dealer',
  tenant: 'Admin',
  staff: 'Staff',
  system: 'System',
}

function TimelineEntry({
  entry,
  onClick,
  hideRole = false,
}: {
  entry: ActionLogEntry
  onClick?: () => void
  // WPS-138 #11: suppress the internal role badge next to a staff name so an
  // internal user is shown by first name only. "You" (the consumer's own
  // entries) still shows - it is not an internal-staff role.
  hideRole?: boolean
}) {
  const { formatDateTime } = useFormatters()
  const initiator =
    entry.initiated_by.type === 'SYSTEM'
      ? 'System'
      : entry.initiated_by.user?.name || entry.initiated_by.user?.email || 'Unknown'
  const isSystem = entry.initiated_by.type === 'SYSTEM'
  const isContact = entry.action_key === 'consumer_contact'
  const isInfoRequest = entry.action_key === 'request_info'
  const isInfoResponse = entry.action_key === 'provide_info'
  const isNote = entry.action_key === 'note' || isContact
  const params = entry.parameters as Record<string, unknown> | null
  const senderRole = params?.sender_role as string | undefined
  const hasAttachments = Array.isArray(params?.attachments) && params.attachments.length > 0
  const hasCapturedValues = entry.captured_values && Object.keys(entry.captured_values).length > 0
  const isClickable =
    (isNote && params?.message) || hasCapturedValues || hasAttachments

  // Info requests/responses read like messages, mirroring the
  // contact-customer treatment (blue request, green response).
  const dotColor = isContact || isInfoRequest
    ? 'border-[var(--blue)]'
    : isNote
      ? 'border-[var(--amber,#d97706)]'
      : 'border-[var(--green)]'

  return (
    <div
      className={cn('relative pb-[18px] pl-3', isClickable && 'cursor-pointer group')}
      onClick={isClickable ? onClick : undefined}
    >
      <div className={cn(
        'absolute left-[-20px] top-1 h-[11px] w-[11px] rounded-full border-[1.5px] bg-[var(--bg)]',
        dotColor,
      )} />
      <div>
        <span className={cn(
          'text-[13px] font-normal text-[var(--ink)]',
          isClickable && 'group-hover:underline',
        )}>
          {(isContact || isInfoRequest) && <Mail className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--blue)]" />}
          {isInfoResponse && <Upload className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--green)]" />}
          {isNote && !isContact && <StickyNote className="mr-1.5 inline-block h-3.5 w-3.5 text-[var(--amber,#d97706)]" />}
          {entry.to_state_label || entry.action_label}
        </span>
        <div className="flex items-center gap-2 mt-1">
          {isSystem ? (
            <Cpu className="h-3 w-3 text-[var(--ink-3)]" />
          ) : isNote && senderRole === 'dealer' ? (
            <Building className="h-3 w-3 text-[var(--ink-3)]" />
          ) : (
            <User className="h-3 w-3 text-[var(--ink-3)]" />
          )}
          <span className="text-[11px] text-[var(--ink-3)]">
            {initiator}
          </span>
          {senderRole && (!hideRole || senderRole === 'consumer') && (
            <span className="text-[10px] font-medium text-[var(--ink-3)] bg-[var(--bg-2,#f5f5f5)] px-1.5 py-0.5 rounded">
              {SENDER_ROLE_LABELS[senderRole] || senderRole}
            </span>
          )}
          <span className="text-[10px] text-[var(--ink-3)]">
            {formatDateTime(entry.created_at)}
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Consumer Notes Panel (read-only, customer-visible) ─

interface ConsumerNote {
  uuid: string
  title: string
  content: string
  category: string
  is_internal: boolean
  created_by_name: string
  created_at: string
  /** Files kept with the note, e.g. a letter's PDF and its enclosures. */
  attachments?: { uuid: string; file_name: string; mime_type: string; url: string | null }[]
}

function ConsumerNotesPanel({
  claimUuid,
  canWrite,
  onAdd,
}: {
  claimUuid: string
  canWrite: boolean
  onAdd?: () => void
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useFormatters()
  const [selectedNote, setSelectedNote] = useState<ConsumerNote | null>(null)

  // Standard query fetch - replaces a hand-rolled useEffect + setState
  // loader that react-hooks/set-state-in-effect rejects. Error behaviour
  // matches the old catch branch: no data renders nothing.
  const { data: notes = [], isLoading: loading } = useQuery<ConsumerNote[]>({
    queryKey: ['consumer-claim-notes', claimUuid],
    queryFn: async () => {
      const res = await api.get(`/claims/${claimUuid}/notes/`)
      return res.data?.results ?? res.data ?? []
    },
  })

  // Don't flash an empty state while loading (empty-state rule). Once settled:
  // a writer (owner OR collaborator) always sees the Notes card so they can add
  // one even before any exist; a read-only view_only contact sees it only when
  // there are notes to read.
  if (loading) return null
  if (notes.length === 0 && !(canWrite && onAdd)) return null

  return (
    <DetailCard
      title="Notes"
      icon={<MessageSquare className="h-3.5 w-3.5" />}
      collapsible={notes.length > 5}
      previewCount={5}
      trailing={
        canWrite && onAdd ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={onAdd}
            className="cursor-pointer text-[var(--ink-3)] hover:text-[var(--ink)]"
          >
            <Plus className="h-3.5 w-3.5" />
            {t('consumerClaim.addNote', 'Add note')}
          </Button>
        ) : undefined
      }
    >
      {notes.length === 0 ? (
        <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
          {t(
            'consumerClaim.noNotesYet',
            'No notes yet. Add a note to share details with the team.',
          )}
        </div>
      ) : (
      <div className="flex flex-col">
        {notes.map((note) => (
          <button
            key={note.uuid}
            type="button"
            onClick={() => setSelectedNote(note)}
            className="flex w-full cursor-pointer items-baseline justify-between gap-4 border-b-[0.5px] border-b-[var(--rule-light)] bg-transparent px-4 py-[9px] text-left transition-colors hover:bg-[var(--bg-subtle)]"
          >
            <div className="flex items-center gap-2 overflow-hidden">
              <span className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[7px] font-normal uppercase tracking-[0.05em] text-[var(--green)]">
                {(note.created_by_name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2)}
              </span>
              <span className="truncate text-[13px] text-[var(--ink)]">
                {note.title || note.content.slice(0, 40) + (note.content.length > 40 ? '…' : '')}
              </span>
              <span className={cn(
                'rounded-[3px] border-[0.5px] px-1.5 py-px text-[9px] font-normal uppercase tracking-[0.1em]',
                'border-[var(--rule-strong)] bg-transparent text-[var(--ink-3)]',
              )}>
                {note.category}
              </span>
            </div>
            <span className="shrink-0 text-[11px] text-[var(--ink-3)]">
              {formatDateTime(note.created_at)}
            </span>
          </button>
        ))}
      </div>
      )}
      <Dialog open={!!selectedNote} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DialogContent className="max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
              {selectedNote?.title || 'Note'}
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center gap-2 text-[11px] text-[var(--ink-3)]">
            <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] text-[8px] font-normal uppercase tracking-[0.05em] text-[var(--green)]">
              {(selectedNote?.created_by_name || '?').split(' ').map((w) => w[0]).join('').slice(0, 2)}
            </span>
            <span>{selectedNote?.created_by_name} · {selectedNote ? formatDateTime(selectedNote.created_at) : ''}</span>
          </div>
          <div className="mt-3 whitespace-pre-wrap text-[13px] leading-[1.7] text-[var(--ink)]">
            {selectedNote?.content}
          </div>
          {selectedNote?.attachments && selectedNote.attachments.length > 0 && (
            <div className="mt-3 flex flex-col gap-1.5" data-testid="consumer-note-attachments">
              <p className="text-[10px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                {t('consumerClaim.noteAttachments', 'Attachments')}
              </p>
              {selectedNote.attachments.map((file) => (
                <a
                  key={file.uuid}
                  href={file.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-[13px] text-[var(--green)] hover:underline"
                >
                  <FileText className="h-3.5 w-3.5" />
                  {file.file_name}
                </a>
              ))}
            </div>
          )}
          {onAdd && (
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setSelectedNote(null)
                  onAdd()
                }}
                className="cursor-pointer"
              >
                <Reply className="h-3.5 w-3.5" />
                Reply
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </DetailCard>
  );
}

// ── Contact Dialog ──────────────────────────────────────

function ContactDialog({
  open,
  onOpenChange,
  orgName,
  claimUuid,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  orgName: string
  claimUuid: string
}) {
  const { t } = useTranslation()
  const contactMutation = useConsumerClaimContact()

  // Send stays enabled; a message under the backend's 10-character minimum
  // is flagged on the field when the customer presses it.
  const fields = useMemo(
    () => [
      {
        type: 'textarea' as const,
        name: 'message',
        label: t('consumerClaim.contact.messageLabel', 'Message'),
        placeholder: t('consumerClaim.contact.placeholder', 'Describe your query or concern...'),
        value: '',
        validationSchema: z
          .string()
          .trim()
          .min(10, t('consumerClaim.contact.tooShort', 'Message must be at least 10 characters')),
      },
    ],
    [t],
  )

  const { Form } = useForm(
    { name: `consumer-claim-contact-${claimUuid}`, submit: () => <></>, fields },
    async (values, ctx) => {
      try {
        await contactMutation.mutateAsync({
          uuid: claimUuid,
          message: String(values.message ?? '').trim(),
        })
        toast.success('Message sent successfully')
        ctx.resetForm()
        onOpenChange(false)
      } catch (err) {
        toastApiError(err, 'Failed to send message')
      }
    },
    [claimUuid, onOpenChange, fields],
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Contact {orgName}
          </DialogTitle>
        </DialogHeader>
        <div className="mt-3">
          <p className="mb-3 text-[12px] text-[var(--ink-3)]">
            Send a message regarding your claim. The organisation will be notified by email.
          </p>
          <Form className="flex flex-col gap-4">
            {() => (
              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                  className="cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={contactMutation.isPending}
                  className="cursor-pointer"
                >
                  {contactMutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  Send Message
                </Button>
              </DialogFooter>
            )}
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Consumer Action Form (inline, not modal) ──────────

function ConsumerActionForm({
  action,
  claimUuid,
  orgName,
}: {
  action: ConsumerAction
  claimUuid: string
  orgName: string
}) {
  const executeMutation = useConsumerClaimExecuteAction()
  const uploadMutation = useConsumerFileUpload()

  const infoRequest = action.info_request
  const isInfoRequest = Boolean(infoRequest)

  // Generated form fields, built from the action's capture metadata. The
  // generated inputs handle file uploads, required markers, and validation
  // (hand-rolled forms are banned - see CLAUDE.md).
  const generatedFields = useMemo(
    () => action.fields.map((field) => buildFormFieldFromMeta(normaliseClaimField(field), undefined)),
    [action.fields],
  )

  const { Form } = useForm(
    {
      name: `consumer-action-${action.key}`,
      submit: () => <></>,
      fields: generatedFields,
      uploadFile: ({ file, use_case }) =>
        uploadMutation.mutateAsync({ file, use_case: use_case || 'CLAIM_EVIDENCE' }),
    },
    async (values) => {
      try {
        await executeMutation.mutateAsync({
          uuid: claimUuid,
          action_key: action.key,
          captured_values: values,
        })
        toast.success(isInfoRequest ? 'Response sent' : `${action.label} completed`)
      } catch (err: unknown) {
        toastApiError(err, `Failed to complete ${action.label}`)
      }
    },
    [claimUuid, action.key, action.label, isInfoRequest, generatedFields],
  )

  return (
    <div
      className={cn(
        'mb-6 overflow-hidden rounded-[8px] border-[0.5px]',
        isInfoRequest
          ? 'border-l-[3px] border-[var(--status-warn)] shadow-[0_1px_3px_var(--status-warn-bg)]'
          : 'border-[var(--rule)]',
      )}
    >
      <div
        className={cn(
          'flex h-[48px] items-center gap-2 px-4',
          isInfoRequest ? 'bg-[var(--status-warn-bg)]' : 'bg-[var(--bg-subtle)]',
        )}
      >
        {isInfoRequest && <Mail className="h-4 w-4 shrink-0 text-[var(--status-warn)]" />}
        <span
          className={cn(
            'truncate text-xs font-medium uppercase tracking-[0.14em]',
            isInfoRequest ? 'text-[var(--status-warn)]' : 'font-normal text-[var(--ink-3)]',
          )}
        >
          {isInfoRequest ? 'More information needed' : action.label}
        </span>
      </div>
      <div className="bg-[var(--bg)] p-5">
        {isInfoRequest && infoRequest?.reason ? (
          <div className="mb-5 rounded-[8px] border-[0.5px] border-[var(--rule-light)] bg-[var(--bg-subtle)] p-4">
            <div className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
              Message from {orgName}
            </div>
            <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-[var(--ink)]">
              {infoRequest.reason}
            </p>
          </div>
        ) : (
          !isInfoRequest && (
            <p className="mb-5 text-[12px] text-[var(--ink-3)]">
              Please complete the following information to continue.
            </p>
          )
        )}
        <Form className="flex flex-col gap-4">
          {() => (
            <div className="mt-1">
              <Button
                type="submit"
                disabled={executeMutation.isPending}
                className="cursor-pointer"
              >
                {executeMutation.isPending && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
                {isInfoRequest ? 'Send response' : action.label}
              </Button>
            </div>
          )}
        </Form>
      </div>
    </div>
  );
}

// ── Submit Documents button (Required Evidence card) ──────

/**
 * WPS-138 #16/#17/#18: submits the claim's evidence request from inside the
 * Required Evidence card (top and bottom). Files are added per slot above; this
 * finalises the upload session, the same step the emailed upload page runs. A
 * required slot still short keeps the claim in Awaiting Documents: the server
 * refuses and the short slots are highlighted. Stays enabled unless in flight.
 */
function SubmitEvidenceButton({
  claimUuid,
  token,
  currentStateKey,
  onShortfalls,
  className,
}: {
  claimUuid: string
  token: string
  currentStateKey: string
  onShortfalls: (shortfalls: SessionShortfall[]) => void
  className?: string
}) {
  const { t } = useTranslation()
  const submit = useSubmitClaimEvidence()

  const handleSubmit = async () => {
    try {
      await submit.mutateAsync({ claimUuid, token, previousStateKey: currentStateKey })
      toast.success(
        t('claimDetail.documentsSubmitted', 'Thank you. Your documents have been submitted.'),
      )
    } catch (err: unknown) {
      if (err instanceof SessionShortfallError) {
        onShortfalls(err.shortfalls)
        toast.error(
          t(
            'claimDetail.documentsMissing',
            'Some required photos or documents are still missing. Add them below, then submit.',
          ),
        )
        return
      }
      toastApiError(
        err,
        t('claimDetail.documentsSubmitFailed', 'Could not submit your documents. Please try again.'),
      )
    }
  }

  return (
    <Button
      type="button"
      className={cn('cursor-pointer', className)}
      disabled={submit.isPending}
      onClick={handleSubmit}
    >
      {submit.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <Upload className="h-3.5 w-3.5" />
      )}
      {t('claimDetail.submitDocuments', 'Submit Documents')}
    </Button>
  )
}

// ── Main Component ──────────────────────────────────────

export default function ClaimDetailPage() {
  const { t } = useTranslation()
  const { uuid } = useParams<{ uuid: string }>()
  const navigate = useNavigate()
  const { data, isLoading, isError } = useConsumerClaimDetail(uuid)
  const { locale, currency } = useFormattingContext()
  const { formatDateTime } = useFormatters()

  const [contactOpen, setContactOpen] = useState(false)
  const [noteEntry, setNoteEntry] = useState<ActionLogEntry | null>(null)
  // A zero-field consumer decision action (e.g. accept / dispute a settlement
  // offer) awaiting the claimant's confirmation before it executes.
  const [pendingAction, setPendingAction] = useState<ConsumerAction | null>(null)
  // WPS-138 #14: the comment a require_comment action collects (e.g. why the
  // homeowner is disputing) and whether an empty submit was attempted.
  const [decisionComment, setDecisionComment] = useState('')
  const [decisionCommentError, setDecisionCommentError] = useState(false)
  // WPS-138 #18: slots the last Submit found short; highlighted until filled.
  const [shortEvidenceSlotIds, setShortEvidenceSlotIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  )
  const handleEvidenceShortfalls = useCallback((shortfalls: SessionShortfall[]) => {
    setShortEvidenceSlotIds(new Set(shortfalls.map((s) => s.slot_uuid)))
    const first = shortfalls[0]?.slot_uuid
    if (first) {
      document
        .getElementById(evidenceSlotAnchorId(first))
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [])
  const decisionMutation = useConsumerClaimExecuteAction()
  const latestIncomingMessageEntry = getLatestIncomingMessageEntry(data?.history ?? [])
  const claimUuid = data?.claim.uuid ?? null

  // Open the confirm dialog fresh: clear any comment/error carried over from a
  // previous action so the box never starts pre-filled or pre-errored.
  const openPendingAction = (action: ConsumerAction) => {
    setDecisionComment('')
    setDecisionCommentError(false)
    setPendingAction(action)
  }
  const closePendingAction = () => {
    setPendingAction(null)
    setDecisionComment('')
    setDecisionCommentError(false)
  }

  const confirmPendingAction = async () => {
    if (!pendingAction || !claimUuid) return
    // WPS-138 #14: never disable the confirm button for a missing comment -
    // validate on click and surface the error on the field instead (house
    // rule). Only a require_comment action gates on the comment.
    const commentField = pendingAction.comment_field
    let captured: Record<string, unknown> = {}
    if (pendingAction.require_comment && commentField) {
      if (!decisionComment.trim()) {
        setDecisionCommentError(true)
        return
      }
      captured = { [commentField]: decisionComment.trim() }
    }
    try {
      await decisionMutation.mutateAsync({
        uuid: claimUuid,
        action_key: pendingAction.key,
        captured_values: captured,
      })
      toast.success(
        t('consumerClaim.actionCompleted', '{{label}} completed', {
          label: pendingAction.label,
        }),
      )
      closePendingAction()
    } catch (err) {
      toastApiError(
        err,
        t('consumerClaim.actionFailed', 'Failed to {{label}}', {
          label: pendingAction.label.toLowerCase(),
        }),
      )
    }
  }

  // States the claim actually visited, from the same action-log history the
  // timeline renders. Drives the internal-state stepper so a shortcut past a
  // state renders SKIPPED rather than a misleading completed check. Memoised
  // above the loading/error early returns (a hook must run unconditionally),
  // guarding on `data` so it stays undefined until the claim resolves.
  const visitedStateKeys = useMemo(() => {
    if (!data) return undefined
    return computeVisitedStateKeys(data.history, {
      states: data.metadata.workflow_states,
      currentStateKey: data.metadata.workflow_instance?.current_state_key ?? '',
    })
  }, [data])

  // Claim notifications for this consumer are surfaced in a persistent panel.
  // We deliberately do NOT mark them read on open - they stay unread (and keep
  // driving the claims-list card dot, per-user and cross-device) until the
  // consumer explicitly marks them read here. See
  // docs/notification-infrastructure.md.
  const { data: unreadNotificationsData } = useConsumerNotifications(
    claimUuid
      ? { record_type: 'claim', record_uuid: claimUuid, status: 'unread' }
      : undefined,
    { enabled: !!claimUuid },
  )
  // Read notifications are the collapsible history. Fetched up front so the
  // panel knows history exists and stays visible once everything is read - it
  // should only disappear when the claim has no notifications at all.
  const { data: readNotificationsData } = useConsumerNotifications(
    claimUuid
      ? { record_type: 'claim', record_uuid: claimUuid, status: 'read' }
      : undefined,
    { enabled: !!claimUuid },
  )
  const unreadNotifications = unreadNotificationsData?.results ?? []
  const readNotifications = readNotificationsData?.results ?? []
  const markNotificationsRead = useMarkConsumerNotificationsRead()
  const notificationItemAction = useConsumerNotificationItemAction()

  const markNotificationRead = (uuid: string) => {
    notificationItemAction.mutate(
      { uuid, action: 'read' },
      {
        onError: (err) =>
          toastApiError(err, t('notifications.error.markRead', 'Failed to mark notification read')),
      },
    )
  }

  const markAllNotificationsRead = () => {
    if (!claimUuid) return
    markNotificationsRead.mutate(
      { record_type: 'claim', record_uuid: claimUuid },
      {
        onError: (err) =>
          toastApiError(err, t('notifications.error.markAllRead', 'Failed to mark notifications read')),
      },
    )
  }

  const dismissNotification = (uuid: string) => {
    notificationItemAction.mutate(
      { uuid, action: 'archive' },
      {
        onError: (err) =>
          toastApiError(err, t('notifications.error.dismiss', 'Failed to dismiss notification')),
      },
    )
  }

  if (isLoading) {
    return (
      <ConsumerShell>
        <div className="flex items-center justify-center py-20">
          <p className="text-[13px] text-[var(--ink-3)]">Loading...</p>
        </div>
      </ConsumerShell>
    );
  }

  if (isError || !data) {
    return (
      <ConsumerShell>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <p className="text-[13px] text-[var(--ink-3)]">Claim not found</p>
          <Button
            variant="link"
            onClick={() => navigate('/claims')}
            className="text-[11px] underline cursor-pointer text-[var(--green)]"
          >
            Back to claims
          </Button>
        </div>
      </ConsumerShell>
    );
  }

  const { claim, metadata, history, organisation } = data
  // WPS-141 — this consumer's portal access on this claim. "owner" is the
  // property owner (unchanged behaviour); "collaborator" is an external
  // contact with full write access; "view_only" is read-only.
  const accessLevel = metadata.access_level
  // Writable unless the consumer is an explicit external view-only contact.
  // A missing access level (older payloads) means the owner, i.e. full access -
  // only "view_only" restricts, so owners/collaborators are never gated out.
  const canWrite = accessLevel !== 'view_only'
  // WPS-143 — opt-in simplified "View Claim" summary (the layout the claim
  // submission email links to). Off by default; a tenant that has not enabled
  // Tenant.settings.simplified_claim_portal_view keeps the full page unchanged.
  const simplified = metadata.simplified_portal_view ?? false
  // WPS-138 #11 - drop the internal role badge next to staff names on this
  // customer-facing surface (timeline + note dialog), so an internal user is
  // shown by first name only. Gated server-side; off by default.
  const hideStaffRole = metadata.hide_staff_role ?? false
  // Canonical human-facing reference the email + claims list show. Fall back to
  // the CLM-<uuid8> slice for older payloads that predate the backend field.
  const claimReference =
    claim.reference || `CLM-${claim.uuid.slice(0, 8).toUpperCase()}`
  const currentStateKey = metadata.workflow_instance?.current_state_key ?? ''
  // WP1-67: claimant-facing STAGE tracker. When the backend supplies stages
  // (cycle-time capability on), the portal shows the 5 lifecycle stages and a
  // claimant-safe status instead of the internal-state stepper.
  const claimStages = metadata.workflow_instance?.stages ?? []
  const currentStageKey = metadata.workflow_instance?.current_stage?.key ?? ''
  const awaitingClaimant = metadata.workflow_instance?.awaiting_claimant ?? false
  const stageStates = claimStages.map((s, i, arr) => ({
    uuid: s.key,
    key: s.key,
    label: s.label,
    color: '#1c5a48',
    is_initial: i === 0,
    is_terminal: i === arr.length - 1,
  }))
  const isTerminal = metadata.workflow_instance?.current_state_is_terminal ?? false
  // WPS-138 #16: the inline checklist this viewer can submit (show_evidence_checklist).
  const submittableChecklist =
    canWrite && (metadata.evidence_checklist?.slots.length ?? 0) > 0
      ? metadata.evidence_checklist
      : null

  // Consumer workflow actions split by shape. Actions that capture input
  // (info requests, document submission, ...) render as inline forms below the
  // stepper. Actions with NO capture fields are decisions the claimant makes in
  // one click - e.g. accept or dispute a settlement offer - and render as
  // buttons in the Actions panel. Both come straight from the workflow rows via
  // the API (no hardcoded action keys), so any tenant's zero-field consumer
  // action is surfaced generically. The backend already omits actions for
  // view_only contacts; the canWrite guard here is defensive.
  const consumerActions = metadata.consumer_actions ?? []
  const formActions = consumerActions.filter(
    (a) => a.actionable && a.fields.length > 0,
  )
  const decisionActions = canWrite
    ? consumerActions.filter((a) => a.actionable && a.fields.length === 0)
    : []

  // Per-attribute field metadata (field_type, options, ...) drives the
  // same field-type-aware rendering the admin uses, so values never leak
  // as "[object Object]".
  const metadataMap = new Map<string, ConsumerActionFieldMeta>()
  for (const f of metadata.attribute_field_metadata ?? []) {
    metadataMap.set(f.code_name, f)
  }

  /** Resolve the canonical field definition for a claim attribute. */
  const fieldFor = (key: string, value: unknown): FormFieldMeta => {
    const meta = metadataMap.get(key)
    return meta
      ? normaliseClaimField(meta)
      : defineField({
          code_name: key,
          display_name: titleCaseKey(key),
          field_type: inferFieldType(key, value),
        })
  }

  const FILE_FIELD_TYPES = new Set(['FILE', 'IMAGE', 'VIDEO', 'DOCUMENT'])

  // Separate file-type attributes from regular ones. File detection is
  // driven by the field_type from metadata first, then the resolved file
  // envelope shape ({ display, value: { file_url } }) so backend-resolved
  // files are always classified correctly regardless of value shape.
  const fileEntries: Array<{ key: string; file_url: string | null; file_name: string }> = []
  const regularEntries: Array<[string, unknown]> = []

  const pushFileEntry = (key: string, item: unknown) => {
    if (item === null || item === undefined || item === '') return
    const rec =
      item && typeof item === 'object' ? (item as Record<string, unknown>) : null
    fileEntries.push({
      key,
      file_url: rec ? entityFileDisplayUrl(rec) || null : null,
      file_name: (rec ? entityFileDisplayName(rec) : '') || 'Attached',
    })
  }

  // Config-driven: the consumer claim endpoint already returns only
  // attributes whose visibility is PUBLIC ("visible to customer"), so the
  // page renders everything it receives. To hide a field from the consumer
  // record, set its visibility on the attribute - no hardcoded key list.
  for (const [key, value] of Object.entries(claim.attribute_values)) {
    const fieldType = (metadataMap.get(key)?.field_type ?? '').toUpperCase()
    const isFileField =
      FILE_FIELD_TYPES.has(fieldType) ||
      FILE_FIELDS.has(key) ||
      isEntityFileObject(value) ||
      (Array.isArray(value) && value.some(isEntityFileObject))

    if (isFileField) {
      const items = Array.isArray(value) ? value : [value]
      const nonEmpty = items.filter((i) => i !== null && i !== undefined && i !== '')
      nonEmpty.forEach((item, i) => {
        pushFileEntry(nonEmpty.length > 1 ? `${key}_${i + 1}` : key, item)
      })
    } else {
      regularEntries.push([key, value])
    }
  }

  return (
    <ConsumerShell>
      {/* Back button */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/claims')}
        className="mb-8 flex items-center gap-1.5 rounded-[4px] px-2 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)] transition-colors cursor-pointer hover:bg-[var(--bg-subtle)]"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Back
      </Button>
      {/* WPS-141 — access-level banners. View-only contacts get a clear
          read-only notice; collaborators (non-owner, full write access) get
          a quieter "shared with you" note. Owners see neither. */}
      {accessLevel === 'view_only' && (
        <div className="mb-6 flex items-start gap-3 rounded-[8px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--status-error)] bg-[var(--bg-light)] px-4 py-3">
          <p className="text-[12px] font-medium text-[var(--ink)]">
            {t(
              'consumerClaim.viewOnlyAccess',
              'You have view-only access to this claim.',
            )}
          </p>
        </div>
      )}
      {accessLevel === 'collaborator' && (
        <div className="mb-6 flex items-start gap-3 rounded-[8px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--green)] bg-[var(--bg-light)] px-4 py-3">
          <p className="text-[12px] text-[var(--ink-3)]">
            {t(
              'consumerClaim.sharedAccess',
              'This claim was shared with you as a contact.',
            )}
          </p>
        </div>
      )}
      {/* Claim header */}
      <div className="mb-6 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-6">
        <div className="flex gap-6">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center overflow-hidden rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            {claim.product_image_url ? (
              <img
                src={claim.product_image_url}
                alt={claim.product_name}
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-10 w-10 text-[var(--ink-3)]" />
            )}
          </div>
          <div className="flex-1">
            {simplified ? (
              <>
                {/* WPS-143 #1: the claim reference is the primary heading, at a
                    larger size; the product name drops to a secondary line. */}
                <p className="mb-1 text-[10px] font-medium uppercase tracking-[0.14em] text-[var(--ink-3)]">
                  {t('consumerClaim.claimReferenceLabel', 'Claim')}
                </p>
                <h1 className="mb-1.5 font-display text-[34px] font-light leading-[1.1] tracking-[0.01em] text-[var(--ink)]">
                  {claimReference}
                </h1>
                {/* The claim's product beside the header. Its catalogue specs
                    stay off this page (WPS-138 #14). */}
                <p className="mb-2 text-[15px] text-[var(--ink-2)]">
                  {claim.product_name}
                </p>
              </>
            ) : (
              <>
                <h1 className="clara-heading mb-1">{claim.product_name}</h1>
                <p className="mb-1 text-[13px] font-mono font-medium tracking-[0.02em] text-[var(--ink-2)]">
                  {claimReference}
                </p>
              </>
            )}
            <p className="mb-3 text-[12px] text-[var(--ink-3)]">
              {claim.claim_schema_name}
              {claim.host?.registration_number && (
                <> &middot; <span className="font-medium tracking-wide">{claim.host.registration_number}</span></>
              )}
              {claim.host?.make && (
                <> &middot; {claim.host.make}</>
              )}
            </p>
            <div className="flex items-center gap-3">
              {metadata.workflow_instance && (
                <StatusBadge
                  status={
                    metadata.workflow_instance.external_status_label ??
                    metadata.workflow_instance.current_state_label
                  }
                  color={metadata.workflow_instance.current_state_color}
                />
              )}
              {organisation.logo_url ? (
                <img
                  src={organisation.logo_url}
                  alt={organisation.name}
                  className="h-4 max-w-[80px] object-contain opacity-60"
                />
              ) : (
                <span className="text-[11px] text-[var(--ink-3)]">
                  {organisation.name}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Workflow stepper. WP1-67: prefer the claimant-facing STAGE tracker
          (5 lifecycle stages) when available so the portal never exposes the
          internal status set; otherwise fall back to the internal stepper.
          WPS-143 #5: hidden entirely in the simplified summary view. */}
      {!simplified &&
        (stageStates.length > 0 && currentStageKey ? (
          <>
            <WorkflowStepper states={stageStates} currentStateKey={currentStageKey} />
            {awaitingClaimant && (
              <div className="mb-6 flex items-start gap-3 rounded-[8px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[#e67e22] bg-[var(--bg-light)] px-4 py-3">
                <p className="text-[12px] font-medium text-[var(--ink)]">
                  We're waiting on you - please provide the requested information
                  to keep your claim moving.
                </p>
              </div>
            )}
          </>
        ) : metadata.workflow_states.length > 0 && currentStateKey ? (
          <WorkflowStepper
            states={metadata.workflow_states}
            currentStateKey={currentStateKey}
            visitedStateKeys={visitedStateKeys}
          />
        ) : null)}
      {/* Consumer actions - inline forms. The backend already omits these for
          view_only access; filtering on canWrite here too is defensive, not
          the real boundary (execute-action still 403s server-side). */}
      {canWrite && formActions.map((action) => (
        <ConsumerActionForm
          key={action.key}
          action={action}
          claimUuid={claim.uuid}
          orgName={organisation.name}
        />
      ))}
      {/* Persistent notifications panel. Unread staff replies stay here until
          the consumer explicitly marks them read / dismisses them; read ones
          collapse into history. Driven by the notification infrastructure -
          never a per-browser localStorage marker. */}
      <NotificationsPanel
        unread={unreadNotifications}
        read={readNotifications}
        onMarkRead={markNotificationRead}
        onMarkAllRead={markAllNotificationsRead}
        onDismiss={dismissNotification}
        markAllPending={markNotificationsRead.isPending}
        renderItemAction={(notification) =>
          notification.category === 'customer_message' && latestIncomingMessageEntry ? (
            <Button
              variant="outline"
              size="xs"
              onClick={() =>
                latestIncomingMessageEntry && setNoteEntry(latestIncomingMessageEntry)
              }
              className="cursor-pointer"
            >
              {t('notifications.view', 'View')}
            </Button>
          ) : null
        }
      />
      {/* WPS-138 #7/#8/#9: flexible vertical layout - the same CSS multi-column
          masonry the admin claim Overview uses, so cards pack into balanced
          columns by height. Claim Details sits upper-left, Actions above Notes,
          and the single consolidated "Required Evidence" section moves BELOW
          this grid (see after the closing div) so evidence never sits above the
          claim's details, warranty, notes, actions and activity. */}
      <div className="columns-1 gap-6 md:columns-2 [&>*]:mb-6 [&>*]:break-inside-avoid">
          <DetailCard
            title="Claim Details"
            icon={<FileText className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            {regularEntries.length > 0 ? (
              regularEntries.map(([key, value]) => {
                const item = buildClaimFieldItem(key, value, metadataMap)
                return (
                  <FieldRow
                    key={key}
                    label={item.field.display_name}
                    value={
                      item.displayValue ??
                      formatDisplayValue(item.field, item.value, { locale, currency })
                    }
                  />
                )
              })
            ) : (
              <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                No claim details
              </div>
            )}
          </DetailCard>

          {/* WPS-138: Warranty card - the holding's active warranty, so the
              claimant can see their coverage without leaving the claim. Renders
              only when the holding has a warranty (entity-presence gated). */}
          {claim.warranty && (
            <DetailCard
              title="Warranty"
              icon={<ShieldCheck className="h-3.5 w-3.5" />}
              collapsible={false}
              trailing={<WarrantyBadge status={claim.warranty.status} />}
            >
              <FieldRow
                label="Coverage"
                value={claim.warranty.option_display_name || claim.warranty.class_name}
              />
              {claim.warranty.class_name &&
                claim.warranty.class_name !== claim.warranty.option_display_name && (
                  <FieldRow label="Plan" value={claim.warranty.class_name} />
                )}
              <FieldRow
                label="Term"
                value={
                  claim.warranty.is_lifetime ? 'Lifetime' : claim.warranty.term || '-'
                }
              />
              {claim.warranty.remaining_time_string && (
                <FieldRow
                  label="Time remaining"
                  value={claim.warranty.remaining_time_string}
                />
              )}
              {claim.warranty.active_from && (
                <FieldRow
                  label="Coverage start"
                  value={formatDisplayValue(
                    defineField({
                      code_name: 'active_from',
                      display_name: 'Coverage start',
                      field_type: 'DATE',
                    }),
                    claim.warranty.active_from,
                    { locale, currency },
                  )}
                />
              )}
              <FieldRow
                label="Expires"
                value={
                  claim.warranty.is_lifetime
                    ? 'Never (lifetime)'
                    : claim.warranty.expires_at
                      ? formatDisplayValue(
                          defineField({
                            code_name: 'expires_at',
                            display_name: 'Expires',
                            field_type: 'DATE',
                          }),
                          claim.warranty.expires_at,
                          { locale, currency },
                        )
                      : '-'
                }
              />
              {(claim.warranty.documents ?? []).map((doc) =>
                doc.url ? (
                  <FieldRow
                    key={doc.uuid}
                    label={doc.label}
                    value="View document"
                    link
                    onLinkClick={() =>
                      window.open(
                        doc.url as string,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  />
                ) : null,
              )}
            </DetailCard>
          )}

          {/* WPS-138 #14: the settlement the homeowner is approving, placed
              next to the Actions so they can see what they are accepting or
              disputing. Rendered only when the API attaches it (opted-in tenant
              + the claim carries a settlement). */}
          {metadata.settlement && (
            <SettlementSummaryCard
              settlement={metadata.settlement}
              locale={locale}
              currency={currency}
            />
          )}

          {/* Actions - WPS-138 #8: above Notes and the Activity timeline so the
              customer's available actions are the first thing they can act on. */}
          <DetailCard
            title="Actions"
            icon={<FileText className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            <div className="flex flex-col gap-2 p-3">
              {/* Decision actions the claimant takes in one click (accept /
                  dispute a settlement offer, acknowledge, ...). These are
                  zero-field consumer workflow actions surfaced from the API -
                  each opens a confirm dialog before it executes. */}
              {decisionActions.map((action) => (
                <Button
                  key={action.key}
                  variant="ghost"
                  onClick={() => openPendingAction(action)}
                  className="flex items-center justify-start gap-3 w-full px-4 py-3 rounded-[4px] text-[12px] transition-colors cursor-pointer text-left bg-[var(--bg-subtle)] hover:bg-[var(--green-bg)] text-[var(--ink)]"
                >
                  <WorkflowActionIcon
                    action={{ key: action.key, label: action.label, icon: action.icon }}
                    fallback={Check}
                    className="h-4 w-4 text-[var(--green)]"
                  />
                  {action.label}
                </Button>
              ))}
              {/* Contact organisation - not available to a view_only contact */}
              {organisation.name && canWrite && (
                <Button
                  variant="ghost"
                  onClick={() => setContactOpen(true)}
                  className="flex items-center justify-start gap-3 w-full px-4 py-3 rounded-[4px] text-[12px] transition-colors cursor-pointer text-left bg-[var(--bg-subtle)] hover:bg-[var(--green-bg)] text-[var(--ink)]"
                >
                  <Mail className="h-4 w-4 text-[var(--green)]" />
                  Contact {organisation.name}
                </Button>
              )}
              {decisionActions.length === 0 && !(organisation.name && canWrite) && (
                <div className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                  No actions available
                </div>
              )}
            </div>
          </DetailCard>

          {/* Notes (customer-visible only) - WPS-138 #8: below the Actions. */}
          <div>
            <ConsumerNotesPanel
              claimUuid={claim.uuid}
              canWrite={canWrite}
              onAdd={canWrite ? () => setContactOpen(true) : undefined}
            />
          </div>

          {/* Activity timeline. The history can be long, so it scrolls inside
              a fixed-height container - the same treatment as the admin claim
              Activity card (max-h-[420px] overflow-y-auto).
              WPS-143 #5: hidden entirely in the simplified summary view. */}
          {!simplified && history.length > 0 && (
            <div>
              <DetailCard
                title="Activity"
                icon={<Clock className="h-3.5 w-3.5" />}
                collapsible={false}
              >
                <div className="max-h-[420px] overflow-y-auto px-5 py-4">
                  <div className="relative ml-5 pl-5">
                    <div className="absolute bottom-1 left-[5px] top-1 w-px bg-[var(--rule)]" />
                    {history.map((entry) => (
                      <TimelineEntry
                        key={entry.uuid}
                        entry={entry}
                        onClick={() => setNoteEntry(entry)}
                        hideRole={hideStaffRole}
                      />
                    ))}
                  </div>
                </div>
              </DetailCard>
            </div>
          )}
      </div>
      {/* WPS-138 #7: the SINGLE, consolidated "Required Evidence" section,
          moved BELOW the cards. It merges what used to be three separate
          surfaces - the dynamic requested-category checklist (where a
          staff-requested document/photo category now appears automatically),
          the read-only list of already-submitted files, and the link-out for
          tenants without the inline checklist. The ad-hoc "Add Photo" uploader
          was removed; uploads flow through the requested slots.
          WPS-138 #13: rendered even when the claim is closed (e.g. closed for
          inactivity) as long as an evidence request is still live, so the
          homeowner can still submit what was asked for - the backend only
          attaches a live upload session, so nothing here is terminal-gated. */}
      {(canWrite &&
        ((metadata.evidence_checklist?.slots.length ?? 0) > 0 ||
          !!metadata.evidence_upload_url)) ||
      fileEntries.length > 0 ? (
        <div className="mt-6">
          <DetailCard
            title={t('claimDetail.evidenceChecklistTitle', 'Required Evidence')}
            icon={<Camera className="h-3.5 w-3.5" />}
            collapsible={false}
          >
            <div className="p-3">
              {/* WPS-138 #16/#17: Submit sits at the top and bottom of the card
                  while the checklist is live, so it never disappears. */}
              {submittableChecklist && (
                <div className="mb-3">
                  <SubmitEvidenceButton
                    claimUuid={claim.uuid}
                    token={submittableChecklist.token}
                    currentStateKey={currentStateKey}
                    onShortfalls={handleEvidenceShortfalls}
                    className="w-full sm:w-auto"
                  />
                </div>
              )}
              {isTerminal &&
                canWrite &&
                ((metadata.evidence_checklist?.slots.length ?? 0) > 0 ||
                  !!metadata.evidence_upload_url) && (
                  <p className="mb-3 rounded-[6px] border-[0.5px] border-l-[3px] border-[var(--rule)] border-l-[var(--status-warning,#e67e22)] bg-[var(--bg-light)] px-3 py-2 text-[12px] text-[var(--ink)]">
                    {t(
                      'claimDetail.evidenceClosedNote',
                      'This claim was closed, but you can still upload the documents we requested.',
                    )}
                  </p>
                )}
              {canWrite && (metadata.evidence_checklist?.slots.length ?? 0) > 0 ? (
                <>
                  <p className="mb-3 text-[12px] text-[var(--ink-3)]">
                    {t(
                      'claimDetail.evidenceChecklistSubtitle',
                      'Add the photos and documents below so we can review your claim.',
                    )}
                  </p>
                  <ConsumerEvidenceChecklist
                    claimUuid={claim.uuid}
                    checklist={metadata.evidence_checklist!}
                    flaggedSlotIds={shortEvidenceSlotIds}
                  />
                </>
              ) : canWrite && metadata.evidence_upload_url ? (
                <a
                  href={metadata.evidence_upload_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mb-3 flex items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--green)] bg-[var(--green-bg-subtle,var(--bg-subtle))] px-4 py-3 no-underline transition-colors hover:border-[var(--green-hover,var(--green))]"
                >
                  <Camera className="h-4 w-4 shrink-0 text-[var(--green)]" />
                  <div className="min-w-0 flex-1">
                    <p className="text-[12px] font-medium text-[var(--ink)]">
                      {t(
                        'claimDetail.evidenceUploadLinkTitle',
                        'Upload required photos and documents',
                      )}
                    </p>
                    <p className="text-[11px] text-[var(--ink-3)]">
                      {t(
                        'claimDetail.evidenceUploadLinkSubtitle',
                        'Open the secure upload page to submit your evidence',
                      )}
                    </p>
                  </div>
                  <ExternalLink className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                </a>
              ) : null}
              {/* Already-submitted evidence (read-only; view/download, no delete). */}
              <div
                className={cn(
                  (metadata.evidence_checklist?.slots.length ?? 0) > 0 ||
                    metadata.evidence_upload_url
                    ? 'mt-4'
                    : '',
                )}
              >
                <p className="mb-2 text-[11px] font-medium uppercase tracking-[0.1em] text-[var(--ink-3)]">
                  {t('claimDetail.submittedEvidence', 'Submitted evidence')}
                </p>
                <EvidenceFileList
                  items={fileEntries.map((file) => ({
                    key: file.key,
                    fileName: file.file_name || 'Attached',
                    fileUrl: file.file_url,
                  }))}
                  emptyMessage={t('claimDetail.noEvidence', 'No evidence uploaded')}
                />
              </div>
              {submittableChecklist && (
                <div className="mt-4 border-t-[0.5px] border-[var(--rule)] pt-3">
                  <SubmitEvidenceButton
                    claimUuid={claim.uuid}
                    token={submittableChecklist.token}
                    currentStateKey={currentStateKey}
                    onShortfalls={handleEvidenceShortfalls}
                    className="w-full sm:w-auto"
                  />
                </div>
              )}
            </div>
          </DetailCard>
        </div>
      ) : null}
      {/* Contact dialog */}
      <ContactDialog
        open={contactOpen}
        onOpenChange={setContactOpen}
        orgName={organisation.name}
        claimUuid={uuid!}
      />
      {/* Note/message/captured-values dialog */}
      <ActionEntryDialog
        entry={noteEntry}
        open={noteEntry !== null}
        onOpenChange={(open) => { if (!open) setNoteEntry(null) }}
        roleLabels={SENDER_ROLE_LABELS}
        hideRoleLabel={hideStaffRole}
        formatValue={(value, key) => {
          if (isEntityFileObject(value)) {
            return entityFileDisplayName(value as Record<string, unknown>) || 'Attached'
          }
          const node = formatDisplayValue(fieldFor(key, value), value, { locale, currency })
          return typeof node === 'string' ? node : String(value ?? '-')
        }}
        formatTimestamp={formatDateTime}
        onReply={() => {
          setNoteEntry(null)
          setContactOpen(true)
        }}
      />
      {/* Confirm dialog for a one-click decision action (accept / dispute) */}
      <Dialog
        open={!!pendingAction}
        onOpenChange={(open) => { if (!open) closePendingAction() }}
      >
        <DialogContent className="max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
              {pendingAction?.label}
            </DialogTitle>
          </DialogHeader>
          <p className="mt-1 text-[13px] leading-[1.6] text-[var(--ink-2)]">
            {t(
              'consumerClaim.decisionActionConfirm',
              'This updates your claim right away. Do you want to continue?',
            )}
          </p>
          {/* WPS-138 #14: reaffirm the settlement total at the point of
              decision, when the claim carries one. */}
          {metadata.settlement && (
            <div className="mt-2 flex items-center justify-between rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
              <span className="text-[12px] text-[var(--ink-3)]">
                {t('claimDetail.settlementTotal', 'Settlement total')}
              </span>
              <span className="text-[13px] font-medium tabular-nums text-[var(--ink)]">
                {formatCurrency(Number(metadata.settlement.total || 0), currency, locale)}
              </span>
            </div>
          )}
          {/* WPS-138 #14: a require_comment action (e.g. dispute) collects a
              required reason. The confirm button stays enabled; an empty submit
              surfaces the error on the field (house rule). */}
          {pendingAction?.require_comment && (
            <div className="mt-3">
              <Label
                htmlFor="decision-comment"
                className="mb-1 block text-[12px] text-[var(--ink-2)]"
              >
                {pendingAction.comment_prompt ||
                  t('claimDetail.decisionCommentPrompt', 'Please tell us more.')}
              </Label>
              <Textarea
                id="decision-comment"
                value={decisionComment}
                onChange={(e) => {
                  setDecisionComment(e.target.value)
                  if (decisionCommentError && e.target.value.trim()) {
                    setDecisionCommentError(false)
                  }
                }}
                rows={3}
                aria-invalid={decisionCommentError || undefined}
                className={cn(
                  decisionCommentError &&
                    'border-[var(--status-error)] ring-1 ring-[var(--status-error)]',
                )}
              />
              {decisionCommentError && (
                <p className="mt-1 text-[11px] text-[var(--status-error)]">
                  {t('claimDetail.decisionCommentRequired', 'A comment is required.')}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={closePendingAction}
              disabled={decisionMutation.isPending}
              className="cursor-pointer"
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              onClick={confirmPendingAction}
              disabled={decisionMutation.isPending}
              className="cursor-pointer"
            >
              {decisionMutation.isPending && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              {pendingAction?.label}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConsumerShell>
  );
}
